import { ELECTRIC_TOLLS } from './invoiceV2';

const dayMs = 86400000;
const present = value => value !== '' && value != null;
const dateTime = value => {
    if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(value) || Number(value.slice(0, 4)) < 1) return NaN;
    const time = Date.parse(`${value}T00:00:00Z`);
    return Number.isFinite(time) && new Date(time).toISOString().slice(0, 10) === value ? time : NaN;
};

export function invoicePeriod(draft) {
    const start = dateTime(draft.fecha_inicio), end = dateTime(draft.fecha_fin);
    const difference = (end - start) / dayMs;
    const days = present(draft.dias_facturados) ? Number(draft.dias_facturados) : difference + 1;
    return { start, end, difference, days, exclusiveEnd: present(draft.dias_facturados) && days === difference };
}

// Preserve the invoice's existing inclusive/exclusive endpoint convention.
export function changeInvoicePeriod(draft, key, value, mode, exclusiveEnd = invoicePeriod(draft).exclusiveEnd) {
    const previous = invoicePeriod(draft);
    const next = { ...draft, [key]: value };
    if (mode === 'dias') {
        const days = key === 'dias_facturados' ? Number(value) : previous.days;
        const start = dateTime(next.fecha_inicio);
        next.dias_facturados = key === 'dias_facturados' ? value : Number.isFinite(days) ? days : '';
        next.fecha_fin = present(next.dias_facturados) && Number.isInteger(days) && days >= 1 && days <= 366 && Number.isFinite(start)
            ? new Date(start + (days - (exclusiveEnd ? 0 : 1)) * dayMs).toISOString().slice(0, 10) : '';
    } else if (present(draft.dias_facturados)) {
        const { difference } = invoicePeriod(next);
        next.dias_facturados = Number.isFinite(difference) && difference >= 0 ? difference + (exclusiveEnd ? 0 : 1) : '';
    }
    return next;
}

export function invoiceReferenceMode(draft = {}) {
    if (present(draft.total_factura_eur)) return 'total';
    return [draft.precios_energia_eur_kwh, draft.precios_potencia].some(values => Object.values(values || {}).some(present)) ? 'precios' : 'total';
}

export function invoiceReviewState(draft, referenceMode = invoiceReferenceMode(draft), periodMode = 'fechas') {
    const required = new Set(['tarifa_atr', 'geo_zone', 'fecha_inicio', periodMode === 'dias' ? 'dias_facturados' : 'fecha_fin',
        'ie_pct', 'iva_pct', 'alquiler_eur', 'bono_social_eur']);
    const issues = {};
    const unusedPeriods = [];
    const number = (key, value, maximum = Infinity, mandatory = true) => {
        if (mandatory) required.add(key);
        if (!present(value)) {
            if (mandatory) issues[key] = 'Completa este dato para comparar.';
            return null;
        }
        const parsed = typeof value === 'boolean' || typeof value === 'object' || String(value).trim() === '' ? NaN : Number(value);
        if (!Number.isFinite(parsed) || parsed < 0 || parsed > maximum) {
            issues[key] = maximum === 100 ? 'Introduce un porcentaje entre 0 y 100.' : 'Introduce un número válido mayor o igual que cero.';
            return null;
        }
        return parsed;
    };
    if (!ELECTRIC_TOLLS.includes(draft.tarifa_atr)) issues.tarifa_atr = 'Selecciona un peaje eléctrico.';
    if (!['PENINSULA', 'BALEARES', 'CANARIAS', 'CEUTA', 'MELILLA'].includes(draft.geo_zone)) issues.geo_zone = 'Selecciona la zona.';
    const period = invoicePeriod(draft);
    if (!Number.isFinite(period.start)) issues.fecha_inicio = 'Indica la fecha de inicio.';
    if (periodMode === 'fechas' && !Number.isFinite(period.end)) issues.fecha_fin = 'Indica la fecha de fin.';
    const periodField = periodMode === 'dias' ? 'dias_facturados' : 'fecha_fin';
    if (periodMode === 'dias' && !present(draft.dias_facturados)) issues.dias_facturados = 'Indica los días facturados.';
    if (present(draft.dias_facturados) && (!Number.isInteger(period.days) || period.days < 1 || period.days > 366)) {
        issues.dias_facturados = 'Introduce entre 1 y 366 días completos.';
    } else if (Number.isFinite(period.start) && Number.isFinite(period.end)) {
        if (period.days < 1 || period.days > 366 || ![period.difference, period.difference + 1].includes(period.days)) {
            issues[periodField] = 'Revisa el periodo: debe durar entre 1 y 366 días y coincidir con las fechas.';
        }
    } else if (periodMode === 'dias' && Number.isFinite(period.start) && present(draft.dias_facturados)) {
        issues.dias_facturados = 'Revisa los días para calcular la fecha final.';
    }
    ['ie_pct', 'iva_pct'].forEach(key => number(key, draft[key], 100));
    ['alquiler_eur', 'bono_social_eur'].forEach(key => number(key, draft[key]));
    ['excedentes_kwh', 'total_descuentos_eur'].forEach(key => number(key, draft[key], Infinity, false));
    if (ELECTRIC_TOLLS.includes(draft.tarifa_atr)) {
        const energyPeriods = Array.from({ length: draft.tarifa_atr === '2.0TD' ? 3 : 6 }, (_, i) => `P${i + 1}`);
        const powerPeriods = energyPeriods.slice(0, draft.tarifa_atr === '2.0TD' ? 2 : 6);
        const periods = (key, names, maximum = Infinity) => names.map(p => number(`${key}.${p}`, draft[key]?.[p], maximum));
        if (draft.reparto_consumo_pct != null) {
            const total = number('consumo_total_kwh', draft.consumo_total_kwh);
            const weights = periods('reparto_consumo_pct', energyPeriods, 100);
            if (total === 0) issues.consumo_total_kwh = 'El consumo total debe ser mayor que cero.';
            if (weights.every(value => value != null) && Math.abs(weights.reduce((sum, value) => sum + value, 0) - 100) > 0.001) {
                issues.reparto_consumo_pct = 'El reparto debe sumar 100 %.';
            }
        } else {
            const values = periods('consumos_kwh', energyPeriods);
            if (values.every(value => value != null) && values.reduce((sum, value) => sum + value, 0) === 0) issues.consumos_kwh = 'El consumo del periodo debe ser mayor que cero.';
        }
        periods('potencias_kw', powerPeriods);
        [['consumos_kwh', energyPeriods], ['potencias_kw', powerPeriods]].forEach(([key, names]) => {
            Object.entries(draft[key] || {}).forEach(([p, value]) => {
                if (!names.includes(p) && present(value) && Number(value) !== 0) {
                    unusedPeriods.push(`${key}.${p}`);
                    issues[`${key}.${p}`] = 'Este periodo no corresponde al peaje seleccionado.';
                }
            });
        });
        if (referenceMode === 'precios') {
            periods('precios_energia_eur_kwh', energyPeriods);
            periods('precios_potencia', powerPeriods);
        }
    }
    if (referenceMode === 'total') number('total_factura_eur', draft.total_factura_eur);
    else {
        required.add('unidad_precio_potencia');
        if (!['eur_kw_dia', 'eur_kw_ano'].includes(draft.unidad_precio_potencia)) issues.unidad_precio_potencia = 'Confirma si el precio es por día o por año.';
        number('precio_excedentes_eur_kwh', draft.precio_excedentes_eur_kwh, Infinity, false);
    }
    return { required, issues, unusedPeriods, complete: Object.keys(issues).length === 0 };
}
